import { colorSort } from "./colorSort";

export const validMtgColors = ["W", "U", "B", "R", "G", "C"] as const;

export type MtgColor = (typeof validMtgColors)[number];

export enum ColorIdentity {
	COLORLESS = "COLORLESS",
	WHITE = "WHITE",
	BLUE = "BLUE",
	BLACK = "BLACK",
	RED = "RED",
	GREEN = "GREEN",
	AZORIUS = "AZORIUS",
	BOROS = "BOROS",
	DIMIR = "DIMIR",
	GRUUL = "GRUUL",
	ORZHOV = "ORZHOV",
	RAKDOS = "RAKDOS",
	SELESNYA = "SELESNYA",
	SIMIC = "SIMIC",
	IZZET = "IZZET",
	GOLGARI = "GOLGARI",
	ESPER = "ESPER",
	GRIXIS = "GRIXIS",
	JUND = "JUND",
	NAYA = "NAYA",
	BANT = "BANT",
	ABZAN = "ABZAN",
	JESKAI = "JESKAI",
	SULTAI = "SULTAI",
	MARDU = "MARDU",
	TEMUR = "TEMUR",
	YORE_TILLER = "YORE_TILLER",
	GLINT_EYE = "GLINT_EYE",
	DUNE_BROOD = "DUNE_BROOD",
	WITCH_MAW = "WITCH_MAW",
	INK_TREADER = "INK_TREADER",
	WUBRG = "WUBRG",
}

export const humanReadableIdentity = {
	[ColorIdentity.COLORLESS]: "Colorless",
	[ColorIdentity.WHITE]: "White",
	[ColorIdentity.BLUE]: "Blue",
	[ColorIdentity.BLACK]: "Black",
	[ColorIdentity.RED]: "Red",
	[ColorIdentity.GREEN]: "Green",
	[ColorIdentity.AZORIUS]: "Azorius",
	[ColorIdentity.BOROS]: "Boros",
	[ColorIdentity.DIMIR]: "Dimir",
	[ColorIdentity.GRUUL]: "Gruul",
	[ColorIdentity.ORZHOV]: "Orzhov",
	[ColorIdentity.RAKDOS]: "Rakdos",
	[ColorIdentity.SELESNYA]: "Selesnya",
	[ColorIdentity.SIMIC]: "Simic",
	[ColorIdentity.IZZET]: "Izzet",
	[ColorIdentity.GOLGARI]: "Golgari",
	[ColorIdentity.ESPER]: "Esper",
	[ColorIdentity.GRIXIS]: "Grixis",
	[ColorIdentity.JUND]: "Jund",
	[ColorIdentity.NAYA]: "Naya",
	[ColorIdentity.BANT]: "Bant",
	[ColorIdentity.ABZAN]: "Abzan",
	[ColorIdentity.JESKAI]: "Jeskai",
	[ColorIdentity.SULTAI]: "Sultai",
	[ColorIdentity.MARDU]: "Mardu",
	[ColorIdentity.TEMUR]: "Temur",
	[ColorIdentity.YORE_TILLER]: "Yore Tiller",
	[ColorIdentity.GLINT_EYE]: "Glint Eye",
	[ColorIdentity.DUNE_BROOD]: "Dune Brood",
	[ColorIdentity.WITCH_MAW]: "Witch Maw",
	[ColorIdentity.INK_TREADER]: "Ink Treader",
	[ColorIdentity.WUBRG]: "WUBRG",
};

export const identityNameToColors: Record<string, MtgColor[]> = {
	colorless: ["C"],
	white: ["W"],
	blue: ["U"],
	black: ["B"],
	red: ["R"],
	green: ["G"],
	azorius: ["W", "U"],
	boros: ["W", "R"],
	dimir: ["U", "B"],
	gruul: ["R", "G"],
	orzhov: ["W", "B"],
	rakdos: ["B", "R"],
	selesnya: ["W", "G"],
	simic: ["U", "G"],
	izzet: ["U", "R"],
	golgari: ["B", "G"],
	esper: ["W", "U", "B"],
	grixis: ["U", "B", "R"],
	jund: ["B", "R", "G"],
	naya: ["R", "G", "W"],
	bant: ["G", "W", "U"],
	abzan: ["W", "B", "G"],
	jeskai: ["U", "R", "W"],
	sultai: ["B", "G", "U"],
	mardu: ["R", "W", "B"],
	temur: ["G", "U", "R"],
	"yore-tiller": ["W", "U", "B", "R"], // artifice
	"glint-eye": ["U", "B", "R", "G"], // chaos
	"dune-brood": ["B", "R", "G", "W"], // aggression
	"witch-maw": ["R", "G", "W", "U"], // altruism
	"ink-treader": ["G", "W", "U", "B"], // growth
	wubrg: ["W", "U", "B", "R", "G"],

	// Alternate names
	chaos: ["U", "B", "R", "G"],
	artifice: ["W", "U", "B", "R"],
	aggression: ["B", "R", "G", "W"],
	altruism: ["R", "G", "W", "U"],
	growth: ["G", "W", "U", "B"],
};

export const colorsToIdentity = (colors: MtgColor[]): ColorIdentity => {
	// If colors is empty, return colorless
	if (colors.length === 0) {
		return ColorIdentity.COLORLESS;
	}

	// If the colors are all the same as one in identityNameToColors, return that
	const uniqueAndSorted = [...new Set(colors)].sort(colorSort);

	for (const [identity, colors] of Object.entries(identityNameToColors)) {
		const identityColors = [...new Set(colors)].sort(colorSort);

		if (
			uniqueAndSorted.every((c) => identityColors.includes(c)) &&
			identityColors.length === uniqueAndSorted.length
		) {
			return ColorIdentity[
				identity.toUpperCase() as keyof typeof ColorIdentity
			];
		}
	}

	return ColorIdentity.COLORLESS;
};
